import { Helmet } from 'react-helmet-async'
import { useEffect } from 'react'
const SEO = ({
  title,
  description,
  canonical,
  keywords,
  schemaMarkup,
  ogImage = '/images/default-og-image.jpg',
  articleType = 'article', // 'article', 'decision', 'guidance', 'treaty'
  publishedDate,
  modifiedDate,
  country = 'UAE',
  lawName,
}) => {
  // Force a re-render of meta tags when route changes
  useEffect(() => {
    // Small delay to ensure proper cleanup and re-rendering
    const timeoutId = setTimeout(() => {
      // Update document title immediately as backup
      if (title) {
        document.title = title
      }
      if (description) {
        let metaDescription = document.querySelector('meta[name="description"]')
        if (metaDescription) {
          metaDescription.setAttribute('content', description)
        } else {
          // Create the meta tag if it doesn't exist
          metaDescription = document.createElement('meta')
          metaDescription.name = 'description'
          metaDescription.content = description
          document.head.appendChild(metaDescription)
        }
      }

      if (canonical) {
        let canonicalLink = document.querySelector('link[rel="canonical"]')

        if (canonicalLink) {
          canonicalLink.setAttribute('href', canonical)
        } else {
          // Create the canonical link if it doesn't exist
          canonicalLink = document.createElement('link')
          canonicalLink.rel = 'canonical'
          canonicalLink.href = canonical
          document.head.appendChild(canonicalLink)
        }
      }
    }, 0)
    return () => clearTimeout(timeoutId)
  }, [title, description, canonical])

  // Generate keywords automatically if not provided
  const generateKeywords = () => {
    if (keywords) return keywords

    const baseKeywords = []

    // Add country-specific keywords
    if (country) {
      baseKeywords.push(`${country} tax law`)
      if (country === 'UAE') {
        baseKeywords.push('UAE corporate tax', 'Emirates tax law')
      }
    }

    // Add law-specific keywords
    if (lawName) {
      if (lawName.includes('cit')) {
        baseKeywords.push('corporate income tax', 'CIT law')
      }
      if (lawName.includes('vat')) {
        baseKeywords.push('value added tax', 'VAT law')
      }
    }

    // Add type-specific keywords
    if (articleType === 'decision') {
      baseKeywords.push('tax decision', 'legal ruling')
    } else if (articleType === 'guidance') {
      baseKeywords.push('tax guidance', 'compliance guide')
    } else if (articleType === 'treaty') {
      baseKeywords.push('tax treaty', 'double taxation agreement')
    }

    baseKeywords.push('GCC tax laws', 'tax compliance')

    return baseKeywords.slice(0, 8).join(', ') // Limit to 8 keywords
  }
  const fullImageUrl = ogImage.startsWith('http')
    ? ogImage
    : `https://gcctaxlaws.com${ogImage}`
  return (
    <Helmet>
      {/* Basic Meta Tags */}
      <title>{title}</title>
      <meta name='description' content={description} />
      <meta name='keywords' content={generateKeywords()} />
      <link rel='canonical' href={canonical} />

      {/* Robots and Indexing */}
      <meta
        name='robots'
        content='index, follow, max-snippet:-1, max-image-preview:large'
      />
      <meta name='googlebot' content='index, follow' />

      {/* Author and Copyright */}
      <meta name='author' content='GCC Tax Laws' />
      <meta
        name='copyright'
        content={`© ${new Date().getFullYear()} GCC Tax Laws. All rights reserved.`}
      />

      {/* Open Graph for Social Media */}
      <meta property='og:title' content={title} key='og:title' />
      <meta
        property='og:description'
        content={description}
        key='og:description'
      />
      <meta property='og:image' content={fullImageUrl} key='og:image' />
      <meta property='og:url' content={canonical} key='og:url' />
      <meta property='og:type' content='article' key='og:type' />
      <meta property='og:site_name' content='GCC Tax Laws' key='og:site_name' />
      <meta property='og:locale' content='en_US' key='og:locale' />
      {publishedDate && (
        <meta
          property='article:published_time'
          content={publishedDate}
          key='article:published_time'
        />
      )}
      {modifiedDate && (
        <meta
          property='article:modified_time'
          content={modifiedDate}
          key='article:modified_time'
        />
      )}
      <meta
        property='article:author'
        content='GCC Tax Laws'
        key='article:author'
      />
      <meta
        property='article:section'
        content='Tax Law'
        key='article:section'
      />
      {country && (
        <meta
          property='article:tag'
          content={`${country} Tax Law`}
          key='article:tag'
        />
      )}

      {/* Twitter Card */}
      <meta
        name='twitter:card'
        content='summary_large_image'
        key='twitter:card'
      />
      <meta name='twitter:title' content={title} key='twitter:title' />
      <meta
        name='twitter:description'
        content={description}
        key='twitter:description'
      />
      <meta name='twitter:image' content={fullImageUrl} key='twitter:image' />
      <meta name='twitter:site' content='@GCCTaxLaws' key='twitter:site' />

      {/* Additional Legal/Tax specific meta tags */}
      <meta name='document-type' content='Legal Document' />
      {country && <meta name='jurisdiction' content={country} />}
      <meta name='content-language' content='en' />

      {/* Structured Data (Schema Markup) */}
      {schemaMarkup && (
        <script type='application/ld+json' key='schema'>
          {JSON.stringify(schemaMarkup, null, 0)}
        </script>
      )}
    </Helmet>
  )
}

export default SEO
